/**
 * Loan calculator core logic.
 *
 * Supports:
 *  - Multiple payment frequencies (monthly, bi-weekly, weekly, quarterly)
 *  - Reducing-balance interest (bank standard)
 *  - Flat-rate interest (NBFI / microfinance)
 *  - Effective annual rate solving for flat-rate loans
 */

export type Frequency = "monthly" | "biweekly" | "weekly" | "quarterly";
export type InterestType = "reducing" | "flat";

export interface LoanInputs {
  principal: number; // BDT
  annualRatePct: number; // e.g. 12 for 12%
  months: number; // tenure in months (canonical)
  frequency: Frequency;
  interestType: InterestType;
  startDate?: Date;
}

export interface PeriodRow {
  index: number;
  date: Date;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
}

export interface YearRow {
  year: number;
  principalPaid: number;
  interestPaid: number;
  balance: number;
}

export interface LoanResult {
  payment: number; // payment per period
  totalInterest: number;
  totalPayment: number;
  totalPeriods: number;
  periodsPerYear: number;
  effectiveAnnualRatePct: number; // for flat: solved equivalent reducing rate; for reducing: equals input
  schedule: PeriodRow[];
  yearly: YearRow[];
}

export type LoanError =
  | "amount_too_small"
  | "tenure_zero"
  | "rate_negative"
  | "missing";

export function validateLoan(inp: Partial<LoanInputs>): { ok: true } | { ok: false; error: LoanError } {
  if (
    inp.principal == null ||
    inp.annualRatePct == null ||
    inp.months == null ||
    inp.frequency == null ||
    inp.interestType == null
  ) {
    return { ok: false, error: "missing" };
  }
  if (inp.principal < 1000) return { ok: false, error: "amount_too_small" };
  if (inp.months < 1) return { ok: false, error: "tenure_zero" };
  if (inp.annualRatePct < 0) return { ok: false, error: "rate_negative" };
  return { ok: true };
}

export function periodsPerYear(f: Frequency): number {
  switch (f) {
    case "monthly":
      return 12;
    case "biweekly":
      return 26;
    case "weekly":
      return 52;
    case "quarterly":
      return 4;
  }
}

/** Total payment periods given canonical tenure in months and the chosen frequency. */
export function totalPeriods(months: number, f: Frequency): number {
  const years = months / 12;
  return Math.max(1, Math.round(years * periodsPerYear(f)));
}

/** Add one period to a date. */
function addPeriod(d: Date, f: Frequency, count = 1): Date {
  const n = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  switch (f) {
    case "monthly":
      n.setMonth(n.getMonth() + count);
      break;
    case "biweekly":
      n.setDate(n.getDate() + 14 * count);
      break;
    case "weekly":
      n.setDate(n.getDate() + 7 * count);
      break;
    case "quarterly":
      n.setMonth(n.getMonth() + 3 * count);
      break;
  }
  return n;
}

/** Reducing-balance per-period payment (EMI formula generalised). */
export function reducingPayment(principal: number, perPeriodRate: number, periods: number): number {
  if (periods <= 0) return 0;
  if (perPeriodRate === 0) return principal / periods;
  const pow = Math.pow(1 + perPeriodRate, periods);
  return (principal * perPeriodRate * pow) / (pow - 1);
}

/**
 * Solve numerically for the per-period rate that produces a given payment.
 * Used to compute the effective rate of a flat-rate loan.
 */
export function solvePerPeriodRate(principal: number, payment: number, periods: number): number {
  if (periods <= 0 || payment <= 0 || principal <= 0) return 0;
  if (payment * periods <= principal + 1e-6) return 0; // no interest
  let lo = 1e-9;
  let hi = 1; // 100% per period upper bound
  for (let i = 0; i < 100; i++) {
    const mid = (lo + hi) / 2;
    const p = reducingPayment(principal, mid, periods);
    if (p > payment) hi = mid;
    else lo = mid;
    if (hi - lo < 1e-10) break;
  }
  return (lo + hi) / 2;
}

export function computeLoan(inp: LoanInputs): LoanResult {
  const ppy = periodsPerYear(inp.frequency);
  const n = totalPeriods(inp.months, inp.frequency);
  const start = inp.startDate ?? new Date();

  let payment: number;
  const schedule: PeriodRow[] = [];
  let effectiveAnnualRatePct = inp.annualRatePct;

  if (inp.interestType === "reducing") {
    const r = inp.annualRatePct / 100 / ppy;
    payment = reducingPayment(inp.principal, r, n);

    let balance = inp.principal;
    for (let i = 1; i <= n; i++) {
      const interest = balance * r;
      let principalPart = payment - interest;
      if (i === n) principalPart = balance; // absorb rounding
      const newBalance = Math.max(0, balance - principalPart);
      const date = addPeriod(start, inp.frequency, i - 1);
      schedule.push({
        index: i,
        date,
        payment: i === n ? principalPart + interest : payment,
        principal: principalPart,
        interest,
        balance: newBalance,
      });
      balance = newBalance;
    }
  } else {
    // Flat rate
    const years = inp.months / 12;
    const totalInterest = inp.principal * (inp.annualRatePct / 100) * years;
    const totalPayable = inp.principal + totalInterest;
    payment = totalPayable / n;

    // Effective annual rate (solve for equivalent reducing rate)
    const rEff = solvePerPeriodRate(inp.principal, payment, n);
    effectiveAnnualRatePct = rEff * ppy * 100;

    // Equal-split schedule: principal portion is principal/n, interest portion is totalInterest/n
    const principalPerPeriod = inp.principal / n;
    const interestPerPeriod = totalInterest / n;
    let balance = inp.principal;
    for (let i = 1; i <= n; i++) {
      const principalPart = i === n ? balance : principalPerPeriod;
      const newBalance = Math.max(0, balance - principalPart);
      schedule.push({
        index: i,
        date: addPeriod(start, inp.frequency, i - 1),
        payment,
        principal: principalPart,
        interest: interestPerPeriod,
        balance: newBalance,
      });
      balance = newBalance;
    }
  }

  // Yearly aggregation by calendar year
  const yearMap = new Map<number, YearRow>();
  for (const row of schedule) {
    const y = row.date.getFullYear();
    const cur =
      yearMap.get(y) ?? { year: y, principalPaid: 0, interestPaid: 0, balance: row.balance };
    cur.principalPaid += row.principal;
    cur.interestPaid += row.interest;
    cur.balance = row.balance;
    yearMap.set(y, cur);
  }
  const yearly = Array.from(yearMap.values()).sort((a, b) => a.year - b.year);

  const totalPayment = schedule.reduce((s, r) => s + r.payment, 0);
  const totalInterest = totalPayment - inp.principal;

  return {
    payment,
    totalInterest,
    totalPayment,
    totalPeriods: n,
    periodsPerYear: ppy,
    effectiveAnnualRatePct,
    schedule,
    yearly,
  };
}

export function scheduleToCSV(schedule: PeriodRow[]): string {
  const header = "Period,Date,Payment,Principal,Interest,Balance";
  const rows = schedule.map((r) =>
    [
      r.index,
      `${r.date.getFullYear()}-${String(r.date.getMonth() + 1).padStart(2, "0")}-${String(r.date.getDate()).padStart(2, "0")}`,
      r.payment.toFixed(2),
      r.principal.toFixed(2),
      r.interest.toFixed(2),
      r.balance.toFixed(2),
    ].join(","),
  );
  return [header, ...rows].join("\n");
}
